import Konva from "konva";
import type { BodyTrackingState } from "./fxBodyTracking";
import type { BodySegmentationState, SegmentationMaskState } from "./fxBodySegmentation";
import { selectBackgroundBlurPacketForOverlay, type FxRenderPacket } from "./fxRuntime";

type SubjectRect = { x: number; y: number; width: number; height: number };

type CanvasBundle = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
};

type FxBackgroundSource =
  | HTMLVideoElement
  | HTMLImageElement
  | HTMLCanvasElement
  | ImageBitmap
  | OffscreenCanvas;

type MaskRenderMode = "segmentation" | "segmentation-blend" | "held-mask" | "fallback-window" | "full-frame";
type MaskDebugMode = "off" | "final" | "raw" | "post";

export type FxBackgroundOverlayEntry = {
  sourceNode: Konva.Node;
  overlayNode: Konva.Image;
  sourceBuffer: CanvasBundle;
  backgroundBuffer: CanvasBundle;
  blurBuffer: CanvasBundle;
  blurDraftBuffer: CanvasBundle;
  subjectBuffer: CanvasBundle;
  compositeBuffer: CanvasBundle;
  maskBuffer: CanvasBundle;
  featherBuffer: CanvasBundle;
  lastGoodMaskBuffer: CanvasBundle;
  lastGoodMaskMs: number;
  maskSampleBuffer: CanvasBundle;
  rawMaskSampleBuffer: CanvasBundle;
  rawMaskPreviewBuffer: CanvasBundle;
  postMaskPreviewBuffer: CanvasBundle;
  maskSampleImageData: ImageData | null;
  rawMaskSampleImageData: ImageData | null;
  maskScratchA: Float32Array | null;
  maskScratchB: Float32Array | null;
  smoothedSubject: SubjectRect | null;
  smoothedMaskConfidence: number;
  smoothedSegmentationBlend: number;
  lastRenderMs: number;
  lastBlurSourceTime: number;
  size: { width: number; height: number };
};

export type FxBackgroundOverlayMap = Map<string, FxBackgroundOverlayEntry>;

type FxCoverTransform = {
  drawX: number;
  drawY: number;
  drawWidth: number;
  drawHeight: number;
};

const OVERLAY_HOLD_MS = 1500;
const MASK_HOLD_MS = 1500;
const MASK_BASE_DILATION_PX = 2;
const MASK_ALPHA_SOFTSTEP_START = 0.4;
const MASK_ALPHA_SOFTSTEP_END = 0.6;
const OVERLAY_DEBUG_MIN_INTERVAL_MS = 250;
const OVERLAY_DEBUG_DUPLICATE_INTERVAL_MS = 900;
const OVERLAY_DEBUG_VERBOSE_INTERVAL_MS = 140;
const overlayDebugStateByElement = new Map<string, { lastMs: number; lastSignature: string }>();

function logOverlayDebug(args: {
  elementId: string;
  nowMs: number;
  playhead?: number;
  packetId?: string | null;
  stageWidth?: number;
  stageHeight?: number;
  sourceWidth?: number;
  sourceHeight?: number;
  overlayWidth?: number;
  overlayHeight?: number;
  blurRadius?: number;
  maskMode?: MaskRenderMode;
  fallbackReason?: string | null;
  compositorAlive: boolean;
  frameMissing?: boolean;
  maskCoverage?: number;
  maskConfidence?: number;
  reason: string;
}): void {
  const {
    elementId,
    nowMs,
    playhead,
    packetId,
    stageWidth,
    stageHeight,
    sourceWidth,
    sourceHeight,
    overlayWidth,
    overlayHeight,
    blurRadius,
    maskMode,
    fallbackReason,
    compositorAlive,
    frameMissing,
    maskCoverage,
    maskConfidence,
    reason,
  } = args;
  const verbose = isVerboseBgLogEnabled();
  const signature = [
    packetId ?? "none",
    reason,
    maskMode ?? "n/a",
    fallbackReason ?? "none",
    compositorAlive ? "alive" : "dead",
    frameMissing ? "missing" : "ok",
    Number.isFinite(overlayWidth) ? String(Math.round(Number(overlayWidth))) : "na",
    Number.isFinite(overlayHeight) ? String(Math.round(Number(overlayHeight))) : "na",
  ].join("|");
  const prevState = overlayDebugStateByElement.get(elementId);
  const sameSignature = prevState?.lastSignature === signature;
  const elapsedMs = prevState ? nowMs - prevState.lastMs : Number.POSITIVE_INFINITY;
  if (!verbose && sameSignature && elapsedMs < OVERLAY_DEBUG_DUPLICATE_INTERVAL_MS) return;
  if (!verbose && !sameSignature && elapsedMs < OVERLAY_DEBUG_MIN_INTERVAL_MS) return;
  if (verbose && sameSignature && elapsedMs < OVERLAY_DEBUG_VERBOSE_INTERVAL_MS) return;
  overlayDebugStateByElement.set(elementId, { lastMs: nowMs, lastSignature: signature });
  try {
    const playheadText = Number.isFinite(playhead) ? Number(playhead).toFixed(3) : "n/a";
    const stageText =
      Number.isFinite(stageWidth) && Number.isFinite(stageHeight)
        ? `${Math.round(Number(stageWidth))}x${Math.round(Number(stageHeight))}`
        : "n/a";
    const sourceText =
      Number.isFinite(sourceWidth) && Number.isFinite(sourceHeight)
        ? `${Math.round(Number(sourceWidth))}x${Math.round(Number(sourceHeight))}`
        : "n/a";
    const overlayText =
      Number.isFinite(overlayWidth) && Number.isFinite(overlayHeight)
        ? `${Math.round(Number(overlayWidth))}x${Math.round(Number(overlayHeight))}`
        : "n/a";
    const blurText = Number.isFinite(blurRadius) ? Number(blurRadius).toFixed(1) : "n/a";
    const maskCoverageText = Number.isFinite(maskCoverage) ? Number(maskCoverage).toFixed(3) : "n/a";
    const maskConfidenceText = Number.isFinite(maskConfidence) ? Number(maskConfidence).toFixed(3) : "n/a";
    console.info(
      `[FX BG] playhead=${playheadText} el=${elementId} packet=${packetId ?? "none"} stage=${stageText} source=${sourceText} overlay=${overlayText} blur=${blurText}px mask=${maskMode ?? "n/a"} maskCov=${maskCoverageText} maskConf=${maskConfidenceText} fallback=${fallbackReason ?? "none"} alive=${String(compositorAlive)} frameMissing=${String(!!frameMissing)} reason=${reason}`
    );
  } catch {}
}

function isVerboseBgLogEnabled(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem("vb_debug_fx_bg_verbose") === "1";
  } catch {
    return false;
  }
}

function shouldHoldOverlay(entry: FxBackgroundOverlayEntry | undefined, nowMs: number): boolean {
  if (!entry) return false;
  if (!Number.isFinite(entry.lastRenderMs) || entry.lastRenderMs <= 0) return false;
  return nowMs - entry.lastRenderMs < OVERLAY_HOLD_MS;
}

function shouldKeepLastCompositedFrame(entry: FxBackgroundOverlayEntry | undefined): boolean {
  if (!entry) return false;
  if (!Number.isFinite(entry.lastRenderMs) || entry.lastRenderMs <= 0) return false;
  return !!entry.overlayNode?.getParent();
}

export function syncBackgroundBlurOverlay(args: {
  elementId: string;
  node: Konva.Node;
  packets: FxRenderPacket[];
  bodyTrackingState?: BodyTrackingState | null;
  bodySegmentationState?: BodySegmentationState | null;
  overlayMap: FxBackgroundOverlayMap;
  renderWidth?: number;
  renderHeight?: number;
  playhead?: number;
  nowMs?: number;
}): boolean {
  const {
    elementId,
    node,
    packets,
    overlayMap,
    bodyTrackingState,
    bodySegmentationState,
    renderWidth,
    renderHeight,
    playhead,
  } = args;
  const nowMs = args.nowMs ?? performance.now();
  const blurPacket = selectBackgroundBlurPacketForOverlay(packets);
  if (!blurPacket) {
    // Normal state for elements without background blur FX — only log in verbose mode
    // to prevent per-frame console spam (was producing 600+ log lines during playback).
    if (isVerboseBgLogEnabled()) {
      logOverlayDebug({
        elementId,
        nowMs,
        playhead,
        compositorAlive: false,
        reason: "no-blur-packet",
      });
    }
    removeBackgroundBlurOverlay(elementId, overlayMap);
    return false;
  }

  const parent = node.getParent();
  if (!parent) {
    const existing = overlayMap.get(elementId);
    const keep = shouldHoldOverlay(existing, nowMs) || shouldKeepLastCompositedFrame(existing);
    if (keep) {
      logOverlayDebug({
        elementId,
        nowMs,
        playhead,
        packetId: blurPacket.fxBlockId,
        overlayWidth: existing?.size.width,
        overlayHeight: existing?.size.height,
        compositorAlive: true,
        frameMissing: true,
        reason: "keep-parent-missing",
      });
      node.opacity(0);
      return true;
    }
    logOverlayDebug({
      elementId,
      nowMs,
      playhead,
      packetId: blurPacket.fxBlockId,
      compositorAlive: false,
      frameMissing: true,
      reason: "parent-missing-dispose",
    });
    removeBackgroundBlurOverlay(elementId, overlayMap);
    return false;
  }

  const source = resolveNodeSource(node, elementId);
  if (!source) {
    const existing = overlayMap.get(elementId);
    const keep = shouldHoldOverlay(existing, nowMs) || shouldKeepLastCompositedFrame(existing);
    if (keep) {
      logOverlayDebug({
        elementId,
        nowMs,
        playhead,
        packetId: blurPacket.fxBlockId,
        overlayWidth: existing?.size.width,
        overlayHeight: existing?.size.height,
        compositorAlive: true,
        frameMissing: true,
        reason: "keep-source-missing",
      });
      node.opacity(0);
      return true;
    }
    logOverlayDebug({
      elementId,
      nowMs,
      playhead,
      packetId: blurPacket.fxBlockId,
      compositorAlive: false,
      frameMissing: true,
      reason: "source-missing-dispose",
    });
    removeBackgroundBlurOverlay(elementId, overlayMap);
    return false;
  }

  const stageSize = resolveStageSize(node, renderWidth, renderHeight);
  const sourceSize = resolveSourceSize(source);
  const size = resolveCompositorSize({
    source,
    node,
    renderWidth: stageSize?.width,
    renderHeight: stageSize?.height,
  });
  if (size.width <= 0 || size.height <= 0) {
    const existing = overlayMap.get(elementId);
    const keep = shouldHoldOverlay(existing, nowMs) || shouldKeepLastCompositedFrame(existing);
    if (keep) {
      logOverlayDebug({
        elementId,
        nowMs,
        playhead,
        packetId: blurPacket.fxBlockId,
        stageWidth: stageSize?.width,
        stageHeight: stageSize?.height,
        sourceWidth: sourceSize.width,
        sourceHeight: sourceSize.height,
        overlayWidth: existing?.size.width,
        overlayHeight: existing?.size.height,
        compositorAlive: true,
        frameMissing: true,
        reason: "keep-size-invalid",
      });
      node.opacity(0);
      return true;
    }
    logOverlayDebug({
      elementId,
      nowMs,
      playhead,
      packetId: blurPacket.fxBlockId,
      stageWidth: stageSize?.width,
      stageHeight: stageSize?.height,
      sourceWidth: sourceSize.width,
      sourceHeight: sourceSize.height,
      compositorAlive: false,
      frameMissing: true,
      reason: "size-invalid-dispose",
    });
    removeBackgroundBlurOverlay(elementId, overlayMap);
    return false;
  }

  let entry = overlayMap.get(elementId);
  if (
    !entry ||
    entry.sourceNode !== node ||
    !entry.overlayNode.getParent() ||
    entry.size.width !== size.width ||
    entry.size.height !== size.height
  ) {
    removeBackgroundBlurOverlay(elementId, overlayMap);
    entry = createOverlayEntry({
      elementId,
      sourceNode: node,
      parent,
      width: size.width,
      height: size.height,
    });
    if (!entry) return false;
    overlayMap.set(elementId, entry);
  }
  const compositorWidth = entry.size.width;
  const compositorHeight = entry.size.height;
  const coverTransform = resolveCoverTransform(source, compositorWidth, compositorHeight);

  const subject = resolveSubjectWindow({
    state: bodyTrackingState ?? null,
    segmentationState: bodySegmentationState ?? null,
    packet: blurPacket,
    coverTransform,
    frameWidth: compositorWidth,
    frameHeight: compositorHeight,
  });
  if (!subject) {
    const keep = shouldHoldOverlay(entry, nowMs) || shouldKeepLastCompositedFrame(entry);
    if (keep) {
      logOverlayDebug({
        elementId,
        nowMs,
        playhead,
        packetId: blurPacket.fxBlockId,
        stageWidth: stageSize?.width,
        stageHeight: stageSize?.height,
        sourceWidth: sourceSize.width,
        sourceHeight: sourceSize.height,
        overlayWidth: entry.size.width,
        overlayHeight: entry.size.height,
        compositorAlive: true,
        frameMissing: true,
        reason: "keep-subject-missing",
      });
      node.opacity(0);
      return true;
    }
    logOverlayDebug({
      elementId,
      nowMs,
      playhead,
      packetId: blurPacket.fxBlockId,
      stageWidth: stageSize?.width,
      stageHeight: stageSize?.height,
      sourceWidth: sourceSize.width,
      sourceHeight: sourceSize.height,
      overlayWidth: entry.size.width,
      overlayHeight: entry.size.height,
      compositorAlive: false,
      frameMissing: true,
      reason: "subject-missing-dispose",
    });
    removeBackgroundBlurOverlay(elementId, overlayMap);
    return false;
  }

  const expandPx = resolveSubjectExpandPx(blurPacket);
  const featherPx = resolveSubjectFeatherPx(blurPacket);
  const temporalAlpha = resolveTemporalSmoothingAlpha(blurPacket);
  const displacementClampPx = resolveSubjectDisplacementClampPx(blurPacket);
  const expandedSubject = expandSubjectRect(subject, expandPx, compositorWidth, compositorHeight);
  const smoothedSubject = smoothSubjectWindow(
    entry.smoothedSubject,
    expandedSubject,
    temporalAlpha,
    displacementClampPx
  );
  entry.smoothedSubject = smoothedSubject;

  const blurRadius = resolveBlurRadius(blurPacket);
  const draftBlur = shouldUseDraftBlur(blurPacket);
  const frameDrawn = drawSourceToCanvas(
    entry.sourceBuffer.ctx,
    source,
    compositorWidth,
    compositorHeight,
    coverTransform
  );
  updateBackgroundBuffer({
    backgroundBuffer: entry.backgroundBuffer,
    sourceBuffer: entry.sourceBuffer,
    width: compositorWidth,
    height: compositorHeight,
    frameDrawn,
  });
  // Blur cache: skip re-blurring if the video frame hasn't changed (same currentTime)
  const sourceVideoTime = (source instanceof HTMLVideoElement) ? source.currentTime : -1;
  const blurCacheValid = sourceVideoTime >= 0 && sourceVideoTime === entry.lastBlurSourceTime;
  if (!blurCacheValid) {
    drawBlurToCanvas({
      targetBuffer: entry.blurBuffer,
      sourceCanvas: entry.backgroundBuffer.canvas,
      draftBuffer: entry.blurDraftBuffer,
      width: compositorWidth,
      height: compositorHeight,
      blurRadius,
      draftMode: draftBlur,
    });
    entry.lastBlurSourceTime = sourceVideoTime;
  }

  const maskReady = renderSubjectMask({
    entry,
    maskBuffer: entry.maskBuffer,
    featherBuffer: entry.featherBuffer,
    maskSampleBuffer: entry.maskSampleBuffer,
    rawMaskSampleBuffer: entry.rawMaskSampleBuffer,
    maskSampleImageData: entry.maskSampleImageData,
    rawMaskSampleImageData: entry.rawMaskSampleImageData,
    segmentationState: bodySegmentationState ?? null,
    fallbackSubject: smoothedSubject,
    packet: blurPacket,
    coverTransform,
    expandPx,
    featherPx,
    temporalAlpha,
    fullScreenBlur: isFullScreenBlurEnabled(blurPacket),
    nowMs,
    width: compositorWidth,
    height: compositorHeight,
  });
  entry.maskSampleImageData = maskReady.maskSampleImageData;
  entry.rawMaskSampleImageData = maskReady.rawMaskSampleImageData;
  entry.maskScratchA = maskReady.maskScratchA;
  entry.maskScratchB = maskReady.maskScratchB;
  if (!maskReady.ready) {
    const existing = overlayMap.get(elementId);
    const keep = shouldHoldOverlay(existing, nowMs) || shouldKeepLastCompositedFrame(existing);
    if (keep) {
      logOverlayDebug({
        elementId,
        nowMs,
        playhead,
        packetId: blurPacket.fxBlockId,
        stageWidth: stageSize?.width,
        stageHeight: stageSize?.height,
        sourceWidth: sourceSize.width,
        sourceHeight: sourceSize.height,
        overlayWidth: compositorWidth,
        overlayHeight: compositorHeight,
        blurRadius,
        maskMode: maskReady.mode,
        maskCoverage: bodySegmentationState?.mask?.coverage,
        maskConfidence: entry.smoothedMaskConfidence,
        fallbackReason: maskReady.fallbackReason,
        compositorAlive: true,
        frameMissing: !frameDrawn,
        reason: "keep-mask-not-ready",
      });
      node.opacity(0);
      return true;
    }
    logOverlayDebug({
      elementId,
      nowMs,
      playhead,
      packetId: blurPacket.fxBlockId,
      stageWidth: stageSize?.width,
      stageHeight: stageSize?.height,
      sourceWidth: sourceSize.width,
      sourceHeight: sourceSize.height,
      overlayWidth: compositorWidth,
      overlayHeight: compositorHeight,
      blurRadius,
      maskMode: maskReady.mode,
      maskCoverage: bodySegmentationState?.mask?.coverage,
      maskConfidence: entry.smoothedMaskConfidence,
      fallbackReason: maskReady.fallbackReason,
      compositorAlive: false,
      frameMissing: !frameDrawn,
      reason: "mask-not-ready-dispose",
    });
    removeBackgroundBlurOverlay(elementId, overlayMap);
    return false;
  }

  composeBackgroundBlurFrame({
    sourceBuffer: entry.sourceBuffer,
    blurBuffer: entry.blurBuffer,
    maskBuffer: entry.maskBuffer,
    subjectBuffer: entry.subjectBuffer,
    compositeBuffer: entry.compositeBuffer,
    width: compositorWidth,
    height: compositorHeight,
    fullScreenBlur: isFullScreenBlurEnabled(blurPacket),
    fullScreenIntensity: resolveFullScreenIntensity(blurPacket),
    fullScreenGlow: resolveFullScreenGlow(blurPacket),
    fullScreenBloom: resolveFullScreenBloom(blurPacket),
    fullScreenVibrance: resolveFullScreenVibrance(blurPacket),
    fullScreenContrast: resolveFullScreenContrast(blurPacket),
    fullScreenVignette: resolveFullScreenVignette(blurPacket),
  });
  const debugMaskMode = resolveMaskDebugMode(blurPacket);
  if (debugMaskMode !== "off") {
    const debugMaskSource =
      debugMaskMode === "raw"
        ? entry.rawMaskPreviewBuffer.canvas
        : debugMaskMode === "post"
          ? entry.postMaskPreviewBuffer.canvas
          : entry.maskBuffer.canvas;
    drawMaskDebugOverlay({
      compositeBuffer: entry.compositeBuffer,
      maskSource: debugMaskSource,
      width: compositorWidth,
      height: compositorHeight,
    });
  }

  // Replace-compositor behavior: overlay owns final output for this element.
  // Keep alpha tied to the already-evaluated node opacity (no extra attenuation).
  const overlayOpacity = clamp01(node.opacity());

  entry.overlayNode.image(entry.compositeBuffer.canvas);
  entry.overlayNode.width(compositorWidth);
  entry.overlayNode.height(compositorHeight);
  entry.overlayNode.opacity(overlayOpacity);
  const useFullFrameTransform =
    !!stageSize &&
    compositorWidth === stageSize.width &&
    compositorHeight === stageSize.height;
  if (useFullFrameTransform) {
    syncOverlayTransformFullFrame(entry.overlayNode, node, compositorWidth, compositorHeight);
  } else {
    syncOverlayTransform(entry.overlayNode, node);
  }
  placeOverlayAbove({ parent, source: node, overlay: entry.overlayNode });
  // Hide base source while blur overlay is active to avoid ghost/double-image blending.
  node.opacity(0);
  entry.lastRenderMs = nowMs;
  logOverlayDebug({
    elementId,
    nowMs,
    playhead,
    packetId: blurPacket.fxBlockId,
    stageWidth: stageSize?.width,
    stageHeight: stageSize?.height,
    sourceWidth: sourceSize.width,
    sourceHeight: sourceSize.height,
    overlayWidth: entry.compositeBuffer.canvas.width,
    overlayHeight: entry.compositeBuffer.canvas.height,
    blurRadius,
    maskMode: maskReady.mode,
    maskCoverage: bodySegmentationState?.mask?.coverage,
    maskConfidence: entry.smoothedMaskConfidence,
    fallbackReason: maskReady.fallbackReason,
    compositorAlive: true,
    frameMissing: !frameDrawn,
    reason: draftBlur ? "composited-draft" : "composited",
  });

  return true;
}

export function cleanupBackgroundBlurOverlays(args: {
  overlayMap: FxBackgroundOverlayMap;
  keepElementIds: Set<string>;
  nowMs?: number;
}): boolean {
  const { overlayMap, keepElementIds } = args;
  let changed = false;
  for (const elementId of overlayMap.keys()) {
    if (keepElementIds.has(elementId)) continue;
    removeBackgroundBlurOverlay(elementId, overlayMap);
    changed = true;
  }
  return changed;
}

export function disposeBackgroundBlurOverlays(overlayMap: FxBackgroundOverlayMap): boolean {
  let changed = false;
  for (const elementId of overlayMap.keys()) {
    removeBackgroundBlurOverlay(elementId, overlayMap);
    changed = true;
  }
  return changed;
}

function createOverlayEntry(args: {
  elementId: string;
  sourceNode: Konva.Node;
  parent: Konva.Container;
  width: number;
  height: number;
}): FxBackgroundOverlayEntry | undefined {
  const { elementId, sourceNode, parent, width, height } = args;
  const sourceBuffer = createCanvasBundle(width, height);
  const backgroundBuffer = createCanvasBundle(width, height);
  const blurBuffer = createCanvasBundle(width, height);
  const blurDraftBuffer = createCanvasBundle(Math.max(1, Math.round(width * 0.5)), Math.max(1, Math.round(height * 0.5)));
  const subjectBuffer = createCanvasBundle(width, height);
  const compositeBuffer = createCanvasBundle(width, height);
  const maskBuffer = createCanvasBundle(width, height);
  const lastGoodMaskBuffer = createCanvasBundle(width, height);
  const featherBuffer = createCanvasBundle(width, height);
  const maskSampleBuffer = createCanvasBundle(1, 1);
  const rawMaskSampleBuffer = createCanvasBundle(1, 1);
  const rawMaskPreviewBuffer = createCanvasBundle(width, height);
  const postMaskPreviewBuffer = createCanvasBundle(width, height);
  if (
    !sourceBuffer ||
    !backgroundBuffer ||
    !blurBuffer ||
    !blurDraftBuffer ||
    !subjectBuffer ||
    !compositeBuffer ||
    !maskBuffer ||
    !lastGoodMaskBuffer ||
    !featherBuffer ||
    !maskSampleBuffer ||
    !rawMaskSampleBuffer ||
    !rawMaskPreviewBuffer ||
    !postMaskPreviewBuffer
  ) {
    return undefined;
  }

  const overlayNode = new Konva.Image({
    id: `fx-bg-overlay-${elementId}`,
    name: "vb-fx-bg-overlay",
    listening: false,
    draggable: false,
    image: compositeBuffer.canvas,
    width,
    height,
  });
  parent.add(overlayNode);

  return {
    sourceNode,
    overlayNode,
    sourceBuffer,
    backgroundBuffer,
    blurBuffer,
    blurDraftBuffer,
    subjectBuffer,
    compositeBuffer,
    maskBuffer,
    featherBuffer,
    lastGoodMaskBuffer,
    lastGoodMaskMs: 0,
    maskSampleBuffer,
    rawMaskSampleBuffer,
    rawMaskPreviewBuffer,
    postMaskPreviewBuffer,
    maskSampleImageData: null,
    rawMaskSampleImageData: null,
    maskScratchA: null,
    maskScratchB: null,
    smoothedSubject: null,
    smoothedMaskConfidence: 0,
    smoothedSegmentationBlend: 0,
    lastRenderMs: 0,
    lastBlurSourceTime: -1,
    size: { width, height },
  };
}

function renderSubjectMask(args: {
  entry: FxBackgroundOverlayEntry;
  maskBuffer: CanvasBundle;
  featherBuffer: CanvasBundle;
  maskSampleBuffer: CanvasBundle;
  rawMaskSampleBuffer: CanvasBundle;
  maskSampleImageData: ImageData | null;
  rawMaskSampleImageData: ImageData | null;
  segmentationState: BodySegmentationState | null;
  fallbackSubject: SubjectRect;
  packet: FxRenderPacket;
  coverTransform: FxCoverTransform;
  expandPx: number;
  featherPx: number;
  temporalAlpha: number;
  fullScreenBlur: boolean;
  nowMs: number;
  width: number;
  height: number;
}): {
  ready: boolean;
  maskSampleImageData: ImageData | null;
  rawMaskSampleImageData: ImageData | null;
  maskScratchA: Float32Array | null;
  maskScratchB: Float32Array | null;
  mode: MaskRenderMode;
  fallbackReason: string | null;
} {
  const {
    entry,
    maskBuffer,
    featherBuffer,
    maskSampleBuffer,
    rawMaskSampleBuffer,
    maskSampleImageData,
    rawMaskSampleImageData,
    segmentationState,
    fallbackSubject,
    packet,
    coverTransform,
    expandPx,
    featherPx,
    temporalAlpha,
    fullScreenBlur,
    nowMs,
    width,
    height,
  } = args;
  const maskCtx = maskBuffer.ctx;
  maskCtx.clearRect(0, 0, width, height);
  entry.rawMaskPreviewBuffer.ctx.clearRect(0, 0, width, height);
  entry.postMaskPreviewBuffer.ctx.clearRect(0, 0, width, height);

  if (fullScreenBlur) {
    return {
      ready: true,
      maskSampleImageData,
      rawMaskSampleImageData,
      maskScratchA: entry.maskScratchA,
      maskScratchB: entry.maskScratchB,
      mode: "full-frame",
      fallbackReason: "full-screen-blur",
    };
  }

  const segmentationEnabled = isSegmentationEnabled(packet);
  const segmentationCandidate = resolveSegmentationMaskCandidate({
    state: segmentationState,
    packet,
    nowMs,
    enabled: segmentationEnabled,
  });
  const segmentationMask = segmentationCandidate.mask;
  if (segmentationMask) {
    const temporalMaskBlend = resolveMaskTemporalBlend(packet);
    const rawConfidence = clamp01(segmentationMask.meanConfidence);
    entry.smoothedMaskConfidence = entry.smoothedMaskConfidence > 0
      ? lerp(entry.smoothedMaskConfidence, rawConfidence, clamp(temporalAlpha, 0.08, 1))
      : rawConfidence;
    const rawBlendWeight = resolveSegmentationBlendWeight({
      state: segmentationState,
      packet,
    });
    entry.smoothedSegmentationBlend = entry.smoothedSegmentationBlend > 0
      ? lerp(entry.smoothedSegmentationBlend, rawBlendWeight, clamp(temporalAlpha, 0.08, 1))
      : rawBlendWeight;
    const blendWeight = clamp01(entry.smoothedSegmentationBlend);
    const sample = renderSegmentationMaskToSampleCanvas({
      sampleBuffer: maskSampleBuffer,
      rawSampleBuffer: rawMaskSampleBuffer,
      imageData: maskSampleImageData,
      rawImageData: rawMaskSampleImageData,
      segmentationMask,
      expandPx,
      outputWidth: Math.max(1, Math.round(Math.abs(coverTransform.drawWidth))),
      outputHeight: Math.max(1, Math.round(Math.abs(coverTransform.drawHeight))),
      // Keep silhouette edges (hair/head) from collapsing on fast motion or low-confidence frames.
      // The softstep pass still controls final feathering.
      confidenceScale: clamp(0.86 + entry.smoothedMaskConfidence * 0.3, 0.82, 1.15),
      scratchA: entry.maskScratchA,
      scratchB: entry.maskScratchB,
      draftMode: packet.qualityTier === "draft",
    });
    const hasPreviousMask = Number.isFinite(entry.lastGoodMaskMs) && entry.lastGoodMaskMs > 0;
    if (hasPreviousMask && temporalMaskBlend > 0.001) {
      // Preserve previous silhouette contribution to avoid transient holes during fast motion.
      maskCtx.save();
      maskCtx.globalAlpha = temporalMaskBlend;
      maskCtx.drawImage(entry.lastGoodMaskBuffer.canvas, 0, 0, width, height);
      maskCtx.restore();
    }
    if (blendWeight < 0.999) {
      maskCtx.save();
      maskCtx.globalAlpha = clamp01(1 - blendWeight);
      drawFallbackSubjectMatte(maskCtx, fallbackSubject);
      maskCtx.restore();
    }
    maskCtx.save();
    maskCtx.imageSmoothingEnabled = true;
    const currentMaskAlpha = clamp(blendWeight * (1 - temporalMaskBlend * 0.7), 0.15, 1);
    maskCtx.globalAlpha = currentMaskAlpha;
    maskCtx.drawImage(
      sample.sampleBuffer.canvas,
      coverTransform.drawX,
      coverTransform.drawY,
      coverTransform.drawWidth,
      coverTransform.drawHeight
    );
    maskCtx.restore();
    // Skip debug preview canvas draws in draft mode — saves ~1-2ms/frame
    if (packet.qualityTier !== "draft") {
      entry.rawMaskPreviewBuffer.ctx.drawImage(
        sample.rawSampleBuffer.canvas,
        coverTransform.drawX,
        coverTransform.drawY,
        coverTransform.drawWidth,
        coverTransform.drawHeight
      );
      entry.postMaskPreviewBuffer.ctx.drawImage(maskBuffer.canvas, 0, 0, width, height);
    }
    applyMaskFeather({
      maskBuffer,
      featherBuffer,
      featherPx,
      width,
      height,
      draftMode: packet.qualityTier === "draft",
    });
    entry.lastGoodMaskBuffer.ctx.clearRect(0, 0, width, height);
    entry.lastGoodMaskBuffer.ctx.drawImage(maskBuffer.canvas, 0, 0, width, height);
    entry.lastGoodMaskMs = nowMs;
    return {
      ready: true,
      maskSampleImageData: sample.imageData,
      rawMaskSampleImageData: sample.rawImageData,
      maskScratchA: sample.scratchA,
      maskScratchB: sample.scratchB,
      mode: blendWeight < 0.999 ? "segmentation-blend" : "segmentation",
      fallbackReason: blendWeight < 0.999 ? "confidence-blend" : null,
    };
  }

  const holdAgeMs = nowMs - entry.lastGoodMaskMs;
  if (Number.isFinite(holdAgeMs) && holdAgeMs >= 0 && holdAgeMs <= MASK_HOLD_MS) {
    const holdProgress = clamp01(holdAgeMs / MASK_HOLD_MS);
    const fallbackMix = smoothstep(0.5, 1, holdProgress);
    maskCtx.drawImage(entry.lastGoodMaskBuffer.canvas, 0, 0, width, height);
    if (fallbackMix > 0.001) {
      maskCtx.save();
      maskCtx.globalAlpha = fallbackMix;
      drawFallbackSubjectMatte(maskCtx, fallbackSubject);
      maskCtx.restore();
    }
    applyMaskFeather({
      maskBuffer,
      featherBuffer,
      featherPx,
      width,
      height,
      draftMode: packet.qualityTier === "draft",
    });
    return {
      ready: true,
      maskSampleImageData,
      rawMaskSampleImageData,
      maskScratchA: entry.maskScratchA,
      maskScratchB: entry.maskScratchB,
      mode: "held-mask",
      fallbackReason: `${segmentationCandidate.reason}:hold-${Math.round(holdAgeMs)}ms`,
    };
  }

  // No rectangle/window visual fallback: keep prior composited frame until a valid mask returns.
  entry.smoothedMaskConfidence = entry.smoothedMaskConfidence > 0
    ? lerp(entry.smoothedMaskConfidence, 0.5, clamp(temporalAlpha, 0.08, 1))
    : 0.5;
  entry.smoothedSegmentationBlend = entry.smoothedSegmentationBlend > 0
    ? lerp(entry.smoothedSegmentationBlend, 0.12, clamp(temporalAlpha, 0.08, 1))
    : 0.12;
  // Fallback-window keeps blur visible while segmentation is reacquiring (important for scrub + long blocks).
  drawFallbackSubjectMatte(maskCtx, fallbackSubject);
  applyMaskFeather({
    maskBuffer,
    featherBuffer,
    featherPx,
    width,
    height,
  });
  entry.postMaskPreviewBuffer.ctx.drawImage(maskBuffer.canvas, 0, 0, width, height);
  return {
    ready: true,
    maskSampleImageData,
    rawMaskSampleImageData,
    maskScratchA: entry.maskScratchA,
    maskScratchB: entry.maskScratchB,
    mode: "fallback-window",
    fallbackReason: `${segmentationCandidate.reason}:window-fallback`,
  };
}

function renderSegmentationMaskToSampleCanvas(args: {
  sampleBuffer: CanvasBundle;
  rawSampleBuffer: CanvasBundle;
  imageData: ImageData | null;
  rawImageData: ImageData | null;
  segmentationMask: SegmentationMaskState;
  expandPx: number;
  outputWidth: number;
  outputHeight: number;
  confidenceScale: number;
  scratchA: Float32Array | null;
  scratchB: Float32Array | null;
  draftMode?: boolean;
}): {
  sampleBuffer: CanvasBundle;
  rawSampleBuffer: CanvasBundle;
  imageData: ImageData;
  rawImageData: ImageData;
  scratchA: Float32Array;
  scratchB: Float32Array;
} {
  const { sampleBuffer, rawSampleBuffer, segmentationMask, expandPx, outputWidth, outputHeight, confidenceScale } = args;
  const width = Math.max(1, segmentationMask.width);
  const height = Math.max(1, segmentationMask.height);
  if (sampleBuffer.canvas.width !== width || sampleBuffer.canvas.height !== height) {
    resizeCanvasBundle(sampleBuffer, width, height);
  }
  if (rawSampleBuffer.canvas.width !== width || rawSampleBuffer.canvas.height !== height) {
    resizeCanvasBundle(rawSampleBuffer, width, height);
  }

  let imageData = args.imageData;
  if (!imageData || imageData.width !== width || imageData.height !== height) {
    imageData = sampleBuffer.ctx.createImageData(width, height);
  }
  let rawImageData = args.rawImageData;
  if (!rawImageData || rawImageData.width !== width || rawImageData.height !== height) {
    rawImageData = rawSampleBuffer.ctx.createImageData(width, height);
  }

  let scratchA = args.scratchA;
  let scratchB = args.scratchB;
  const pixelCount = width * height;
  if (!scratchA || scratchA.length !== pixelCount) scratchA = new Float32Array(pixelCount);
  if (!scratchB || scratchB.length !== pixelCount) scratchB = new Float32Array(pixelCount);

  const expansionRadius = resolveMaskExpansionRadius({
    expandPx,
    maskWidth: width,
    maskHeight: height,
    outputWidth,
    outputHeight,
  });
  const rawOut = rawImageData.data;
  const safeRange = Math.max(0.00001, MASK_ALPHA_SOFTSTEP_END - MASK_ALPHA_SOFTSTEP_START);
  const count = Math.min(scratchA.length, pixelCount);
  for (let i = 0; i < count; i += 1) {
    const confidence = clamp01(segmentationMask.alpha[i] * confidenceScale);
    let v = 0;
    if (confidence >= MASK_ALPHA_SOFTSTEP_END) {
      v = 1;
    } else if (confidence > MASK_ALPHA_SOFTSTEP_START) {
      const t = (confidence - MASK_ALPHA_SOFTSTEP_START) / safeRange;
      v = t * t * (3 - 2 * t);
    }
    scratchA[i] = v;
    const offset = i * 4;
    rawOut[offset] = 255;
    rawOut[offset + 1] = 255;
    rawOut[offset + 2] = 255;
    rawOut[offset + 3] = Math.round(v * 255);
  }
  rawSampleBuffer.ctx.putImageData(rawImageData, 0, 0);

  // Skip morphological closing in draft mode — saves ~1-2ms/frame (edge cleanup is cosmetic)
  const closingRadius = resolveMaskClosingRadius(expansionRadius);
  if (!args.draftMode) {
    applyDilatePass(scratchA, scratchB, width, height, closingRadius);
    applyErodePass(scratchB, scratchA, width, height, closingRadius);
  }
  if (expansionRadius > 0) {
    applyDilatePass(scratchA, scratchB, width, height, expansionRadius);
  } else {
    for (let i = 0; i < count; i += 1) scratchB[i] = scratchA[i];
  }

  const out = imageData.data;
  for (let i = 0; i < count; i += 1) {
    const v = clamp01(scratchB[i]);
    const offset = i * 4;
    out[offset] = 255;
    out[offset + 1] = 255;
    out[offset + 2] = 255;
    out[offset + 3] = Math.round(v * 255);
  }

  sampleBuffer.ctx.putImageData(imageData, 0, 0);
  return { sampleBuffer, rawSampleBuffer, imageData, rawImageData, scratchA, scratchB };
}

function applyMaskFeather(args: {
  maskBuffer: CanvasBundle;
  featherBuffer: CanvasBundle;
  featherPx: number;
  width: number;
  height: number;
  draftMode?: boolean;
}): void {
  const { maskBuffer, featherBuffer, featherPx, width, height, draftMode } = args;
  const safeFeather = clamp(featherPx, 0, 24);
  if (safeFeather <= 0.01) return;

  // Draft mode: skip feather entirely — saves ~3-5ms/frame from two CSS blur passes
  if (draftMode) return;

  const featherCtx = featherBuffer.ctx;
  featherCtx.clearRect(0, 0, width, height);
  featherCtx.save();
  featherCtx.filter = `blur(${Math.max(0.5, safeFeather * 0.55)}px)`;
  featherCtx.drawImage(maskBuffer.canvas, 0, 0, width, height);
  featherCtx.globalAlpha = 0.55;
  featherCtx.filter = `blur(${safeFeather}px)`;
  featherCtx.drawImage(maskBuffer.canvas, 0, 0, width, height);
  featherCtx.restore();

  const maskCtx = maskBuffer.ctx;
  maskCtx.save();
  maskCtx.globalCompositeOperation = "source-over";
  maskCtx.drawImage(featherBuffer.canvas, 0, 0, width, height);
  maskCtx.restore();
}

function resolveMaskExpansionRadius(args: {
  expandPx: number;
  maskWidth: number;
  maskHeight: number;
  outputWidth: number;
  outputHeight: number;
}): number {
  const { expandPx, maskWidth, maskHeight, outputWidth, outputHeight } = args;
  const sx = maskWidth / Math.max(1, outputWidth);
  const sy = maskHeight / Math.max(1, outputHeight);
  const scale = Math.max(sx, sy);
  const effectiveExpandPx = Math.max(0, expandPx) + MASK_BASE_DILATION_PX;
  return Math.max(0, Math.round(effectiveExpandPx * scale));
}

function resolveMaskClosingRadius(expansionRadius: number): number {
  return Math.max(1, Math.min(3, Math.round(Math.max(1, expansionRadius) * 0.5)));
}

/**
 * O(n) centered sliding-window maximum over a 1D scanline (van Herk/Gil-Werman).
 * Computes max of window [max(0, i-r), min(length-1, i+r)] for each output position i.
 */
// Module-level reusable buffers to avoid per-call Float32Array allocations
let _slidingPrefix: Float32Array | null = null;
let _slidingSuffix: Float32Array | null = null;

function slidingMax1D(
  src: Float32Array, srcOffset: number, srcStride: number,
  dst: Float32Array, dstOffset: number, dstStride: number,
  length: number, radius: number
): void {
  const w = 2 * radius + 1;
  // Reuse module-level buffers; only reallocate when length grows
  if (!_slidingPrefix || _slidingPrefix.length < length) {
    _slidingPrefix = new Float32Array(length);
    _slidingSuffix = new Float32Array(length);
  }
  const prefix = _slidingPrefix;
  const suffix = _slidingSuffix!;

  for (let blockStart = 0; blockStart < length; blockStart += w) {
    const blockEnd = Math.min(blockStart + w, length);
    prefix[blockStart] = clamp01(src[srcOffset + blockStart * srcStride] ?? 0);
    for (let i = blockStart + 1; i < blockEnd; i++) {
      prefix[i] = Math.max(prefix[i - 1], clamp01(src[srcOffset + i * srcStride] ?? 0));
    }
    suffix[blockEnd - 1] = clamp01(src[srcOffset + (blockEnd - 1) * srcStride] ?? 0);
    for (let i = blockEnd - 2; i >= blockStart; i--) {
      suffix[i] = Math.max(suffix[i + 1], clamp01(src[srcOffset + i * srcStride] ?? 0));
    }
  }

  for (let i = 0; i < length; i++) {
    const lo = Math.max(0, i - radius);
    const hi = Math.min(length - 1, i + radius);
    dst[dstOffset + i * dstStride] = Math.max(suffix[lo], prefix[hi]);
  }
}

/**
 * O(n) centered sliding-window minimum over a 1D scanline (van Herk/Gil-Werman).
 */
// Reuse same module-level buffers as slidingMax1D (never called concurrently)
let _slidingMinPrefix: Float32Array | null = null;
let _slidingMinSuffix: Float32Array | null = null;

function slidingMin1D(
  src: Float32Array, srcOffset: number, srcStride: number,
  dst: Float32Array, dstOffset: number, dstStride: number,
  length: number, radius: number
): void {
  const w = 2 * radius + 1;
  if (!_slidingMinPrefix || _slidingMinPrefix.length < length) {
    _slidingMinPrefix = new Float32Array(length);
    _slidingMinSuffix = new Float32Array(length);
  }
  const prefix = _slidingMinPrefix;
  const suffix = _slidingMinSuffix!;

  for (let blockStart = 0; blockStart < length; blockStart += w) {
    const blockEnd = Math.min(blockStart + w, length);
    prefix[blockStart] = clamp01(src[srcOffset + blockStart * srcStride] ?? 0);
    for (let i = blockStart + 1; i < blockEnd; i++) {
      prefix[i] = Math.min(prefix[i - 1], clamp01(src[srcOffset + i * srcStride] ?? 0));
    }
    suffix[blockEnd - 1] = clamp01(src[srcOffset + (blockEnd - 1) * srcStride] ?? 0);
    for (let i = blockEnd - 2; i >= blockStart; i--) {
      suffix[i] = Math.min(suffix[i + 1], clamp01(src[srcOffset + i * srcStride] ?? 0));
    }
  }

  for (let i = 0; i < length; i++) {
    const lo = Math.max(0, i - radius);
    const hi = Math.min(length - 1, i + radius);
    dst[dstOffset + i * dstStride] = Math.min(suffix[lo], prefix[hi]);
  }
}

// Reusable temp buffer for morphological passes (avoids allocation per call)
let _morphTempBuffer: Float32Array | null = null;

function applyDilatePass(
  source: Float32Array,
  target: Float32Array,
  width: number,
  height: number,
  radius: number
): void {
  const safeRadius = Math.max(0, Math.round(radius));
  const count = width * height;
  if (safeRadius <= 0) {
    for (let i = 0; i < count; i += 1) target[i] = clamp01(source[i] ?? 0);
    return;
  }
  // Separable O(n) morphological dilation via van Herk/Gil-Werman
  if (!_morphTempBuffer || _morphTempBuffer.length < count) {
    _morphTempBuffer = new Float32Array(count);
  }
  const temp = _morphTempBuffer;
  // Horizontal pass: each row
  for (let y = 0; y < height; y++) {
    slidingMax1D(source, y * width, 1, temp, y * width, 1, width, safeRadius);
  }
  // Vertical pass: each column
  for (let x = 0; x < width; x++) {
    slidingMax1D(temp, x, width, target, x, width, height, safeRadius);
  }
}

function applyErodePass(
  source: Float32Array,
  target: Float32Array,
  width: number,
  height: number,
  radius: number
): void {
  const safeRadius = Math.max(0, Math.round(radius));
  const count = width * height;
  if (safeRadius <= 0) {
    for (let i = 0; i < count; i += 1) target[i] = clamp01(source[i] ?? 0);
    return;
  }
  // Separable O(n) morphological erosion via van Herk/Gil-Werman
  if (!_morphTempBuffer || _morphTempBuffer.length < count) {
    _morphTempBuffer = new Float32Array(count);
  }
  const temp = _morphTempBuffer;
  // Horizontal pass: each row
  for (let y = 0; y < height; y++) {
    slidingMin1D(source, y * width, 1, temp, y * width, 1, width, safeRadius);
  }
  // Vertical pass: each column
  for (let x = 0; x < width; x++) {
    slidingMin1D(temp, x, width, target, x, width, height, safeRadius);
  }
}


function composeBackgroundBlurFrame(args: {
  sourceBuffer: CanvasBundle;
  blurBuffer: CanvasBundle;
  maskBuffer: CanvasBundle;
  subjectBuffer: CanvasBundle;
  compositeBuffer: CanvasBundle;
  width: number;
  height: number;
  fullScreenBlur: boolean;
  fullScreenIntensity: number;
  fullScreenGlow: number;
  fullScreenBloom: number;
  fullScreenVibrance: number;
  fullScreenContrast: number;
  fullScreenVignette: number;
}): void {
  const {
    sourceBuffer,
    blurBuffer,
    maskBuffer,
    subjectBuffer,
    compositeBuffer,
    width,
    height,
    fullScreenBlur,
    fullScreenIntensity,
    fullScreenGlow,
    fullScreenBloom,
    fullScreenVibrance,
    fullScreenContrast,
    fullScreenVignette,
  } = args;

  const compositeCtx = compositeBuffer.ctx;
  compositeCtx.clearRect(0, 0, width, height);

  if (fullScreenBlur) {
    const intensityMix = clamp(fullScreenIntensity, 0, 1);
    const styleGain = lerp(0.5, 1.6, intensityMix);
    compositeCtx.globalCompositeOperation = "source-over";
    compositeCtx.drawImage(blurBuffer.canvas, 0, 0, width, height);

    if (fullScreenContrast > 0.001) {
      compositeCtx.save();
      compositeCtx.globalCompositeOperation = "overlay";
      compositeCtx.globalAlpha = clamp((0.1 + fullScreenContrast * 0.26) * styleGain, 0, 0.44);
      compositeCtx.filter = `contrast(${(1 + fullScreenContrast * 1.2).toFixed(3)})`;
      compositeCtx.drawImage(sourceBuffer.canvas, 0, 0, width, height);
      compositeCtx.restore();
    }

    if (fullScreenGlow > 0.001) {
      compositeCtx.save();
      compositeCtx.globalCompositeOperation = "screen";
      compositeCtx.globalAlpha = clamp((0.12 + fullScreenGlow * 0.42) * styleGain, 0, 0.62);
      compositeCtx.filter = `blur(${(2 + fullScreenGlow * 14 + intensityMix * 6).toFixed(2)}px)`;
      compositeCtx.drawImage(blurBuffer.canvas, 0, 0, width, height);
      compositeCtx.restore();
    }

    if (fullScreenBloom > 0.001) {
      compositeCtx.save();
      compositeCtx.globalCompositeOperation = "screen";
      compositeCtx.globalAlpha = clamp((0.1 + fullScreenBloom * 0.28) * styleGain, 0, 0.5);
      compositeCtx.filter = `brightness(${(1 + fullScreenBloom * 0.85).toFixed(3)}) saturate(${(
        1 + fullScreenBloom * 0.4
      ).toFixed(3)})`;
      compositeCtx.drawImage(sourceBuffer.canvas, 0, 0, width, height);
      compositeCtx.restore();
    }

    if (fullScreenVibrance > 0.001) {
      compositeCtx.save();
      compositeCtx.globalCompositeOperation = "source-over";
      compositeCtx.globalAlpha = clamp((fullScreenVibrance * 0.2) * styleGain, 0, 0.32);
      compositeCtx.filter = `saturate(${(1 + fullScreenVibrance + intensityMix * 0.24).toFixed(3)})`;
      compositeCtx.drawImage(blurBuffer.canvas, 0, 0, width, height);
      compositeCtx.restore();
    }

    if (fullScreenVignette > 0.001) {
      const centerX = width * 0.5;
      const centerY = height * 0.5;
      const innerRadius = Math.min(width, height) * lerp(0.34, 0.58, 1 - fullScreenVignette * 0.8);
      const outerRadius = Math.max(width, height) * 0.76;
      const vignette = compositeCtx.createRadialGradient(centerX, centerY, innerRadius, centerX, centerY, outerRadius);
      vignette.addColorStop(0, "rgba(0,0,0,0)");
      vignette.addColorStop(1, `rgba(0,0,0,${clamp(0.14 + fullScreenVignette * 0.54, 0, 0.62).toFixed(3)})`);
      compositeCtx.save();
      compositeCtx.globalCompositeOperation = "multiply";
      compositeCtx.fillStyle = vignette;
      compositeCtx.fillRect(0, 0, width, height);
      compositeCtx.restore();
    }
    return;
  }

  const subjectCtx = subjectBuffer.ctx;
  subjectCtx.clearRect(0, 0, width, height);
  subjectCtx.globalCompositeOperation = "source-over";
  subjectCtx.drawImage(sourceBuffer.canvas, 0, 0, width, height);
  subjectCtx.globalCompositeOperation = "destination-in";
  subjectCtx.drawImage(maskBuffer.canvas, 0, 0, width, height);
  subjectCtx.globalCompositeOperation = "source-over";

  compositeCtx.globalCompositeOperation = "source-over";
  compositeCtx.drawImage(blurBuffer.canvas, 0, 0, width, height);
  compositeCtx.globalCompositeOperation = "destination-out";
  compositeCtx.drawImage(maskBuffer.canvas, 0, 0, width, height);
  compositeCtx.globalCompositeOperation = "source-over";
  compositeCtx.drawImage(subjectBuffer.canvas, 0, 0, width, height);
}

function drawSourceToCanvas(
  ctx: CanvasRenderingContext2D,
  source: FxBackgroundSource,
  width: number,
  height: number,
  coverTransform: FxCoverTransform
): boolean {
  ctx.globalCompositeOperation = "source-over";
  try {
    ctx.drawImage(
      source,
      coverTransform.drawX,
      coverTransform.drawY,
      coverTransform.drawWidth,
      coverTransform.drawHeight
    );
    return true;
  } catch {
    // Keep last valid frame to avoid flashing when a video frame isn't ready.
    return false;
  }
}

function updateBackgroundBuffer(args: {
  backgroundBuffer: CanvasBundle;
  sourceBuffer: CanvasBundle;
  width: number;
  height: number;
  frameDrawn: boolean;
}): void {
  const { backgroundBuffer, sourceBuffer, width, height, frameDrawn } = args;
  if (!frameDrawn) return;
  const bgCtx = backgroundBuffer.ctx;
  bgCtx.clearRect(0, 0, width, height);
  bgCtx.globalCompositeOperation = "source-over";
  bgCtx.drawImage(sourceBuffer.canvas, 0, 0, width, height);
}

function drawBlurToCanvas(args: {
  targetBuffer: CanvasBundle;
  sourceCanvas: HTMLCanvasElement;
  draftBuffer: CanvasBundle;
  width: number;
  height: number;
  blurRadius: number;
  draftMode: boolean;
}): void {
  const { targetBuffer, sourceCanvas, draftBuffer, width, height, blurRadius, draftMode } = args;
  const ctx = targetBuffer.ctx;
  ctx.clearRect(0, 0, width, height);
  const safeBlur = clamp(blurRadius, 0, 42);
  if (draftMode) {
    const draftWidth = Math.max(2, Math.round(width * 0.5));
    const draftHeight = Math.max(2, Math.round(height * 0.5));
    resizeCanvasBundle(draftBuffer, draftWidth, draftHeight);
    const draftCtx = draftBuffer.ctx;
    draftCtx.clearRect(0, 0, draftWidth, draftHeight);
    draftCtx.drawImage(sourceCanvas, 0, 0, draftWidth, draftHeight);
    if (safeBlur > 0.01) {
      draftCtx.save();
      draftCtx.filter = `blur(${Math.max(0.75, safeBlur * 0.75)}px)`;
      draftCtx.drawImage(draftBuffer.canvas, 0, 0, draftWidth, draftHeight);
      draftCtx.restore();
    }
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(draftBuffer.canvas, 0, 0, width, height);
    return;
  }
  if (safeBlur > 0.01) {
    ctx.save();
    ctx.filter = `blur(${safeBlur}px)`;
    ctx.drawImage(sourceCanvas, 0, 0, width, height);
    ctx.restore();
    return;
  }
  ctx.drawImage(sourceCanvas, 0, 0, width, height);
}

function removeBackgroundBlurOverlay(elementId: string, overlayMap: FxBackgroundOverlayMap): void {
  const entry = overlayMap.get(elementId);
  if (!entry) return;
  try {
    entry.overlayNode.destroy();
  } catch {}
  overlayMap.delete(elementId);
  overlayDebugStateByElement.delete(elementId);
}

function createCanvasBundle(width: number, height: number): CanvasBundle | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) return null;
  return { canvas, ctx };
}

function resizeCanvasBundle(bundle: CanvasBundle, width: number, height: number): void {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  if (bundle.canvas.width === w && bundle.canvas.height === h) return;
  bundle.canvas.width = w;
  bundle.canvas.height = h;
}

function syncOverlayTransform(overlay: Konva.Image, source: Konva.Node): void {
  const overlayAny = overlay as any;
  const sourceAny = source as any;
  overlay.x(source.x());
  overlay.y(source.y());
  overlay.scaleX(source.scaleX());
  overlay.scaleY(source.scaleY());
  overlay.rotation(source.rotation());
  overlay.offsetX(source.offsetX());
  overlay.offsetY(source.offsetY());
  overlay.visible(source.visible());
  overlay.listening(false);
  if (typeof overlayAny.skewX === "function" && typeof sourceAny.skewX === "function") {
    overlayAny.skewX(sourceAny.skewX());
  }
  if (typeof overlayAny.skewY === "function" && typeof sourceAny.skewY === "function") {
    overlayAny.skewY(sourceAny.skewY());
  }
}

function syncOverlayTransformFullFrame(
  overlay: Konva.Image,
  source: Konva.Node,
  width: number,
  height: number
): void {
  const overlayAny = overlay as any;
  overlay.x(0);
  overlay.y(0);
  overlay.scaleX(1);
  overlay.scaleY(1);
  overlay.rotation(0);
  overlay.offsetX(0);
  overlay.offsetY(0);
  overlay.width(width);
  overlay.height(height);
  overlay.visible(source.visible());
  overlay.listening(false);
  if (typeof overlayAny.skewX === "function") {
    overlayAny.skewX(0);
  }
  if (typeof overlayAny.skewY === "function") {
    overlayAny.skewY(0);
  }
}

function placeOverlayAbove(args: { parent: Konva.Container; source: Konva.Node; overlay: Konva.Node }): void {
  const { parent, source, overlay } = args;
  const nextIndex = Math.min(parent.getChildren().length - 1, source.getZIndex() + 1);
  if (overlay.getZIndex() !== nextIndex) {
    overlay.zIndex(nextIndex);
  }
}

function resolveNodeSource(
  node: Konva.Node,
  elementId?: string
): FxBackgroundSource | null {
  const nodeAny = node as any;
  const direct = resolveImageFromNode(nodeAny);
  if (direct) return direct;

  const child = resolveImageFromChildren(nodeAny, elementId);
  if (child) return child;

  if (typeof nodeAny.toCanvas === "function") {
    try {
      const canvas = nodeAny.toCanvas();
      if (canvas instanceof HTMLCanvasElement) return canvas;
    } catch {}
  }
  return null;
}

function resolveImageFromNode(nodeAny: any): FxBackgroundSource | null {
  if (!nodeAny) return null;
  const image = typeof nodeAny.image === "function" ? nodeAny.image() : null;
  const attrVideo = typeof nodeAny.getAttr === "function" ? nodeAny.getAttr("vbVideo") : null;
  const attrSource = typeof nodeAny.getAttr === "function" ? nodeAny.getAttr("vbSource") : null;
  return resolveCanvasSource(image) ?? resolveCanvasSource(attrVideo) ?? resolveCanvasSource(attrSource);
}

function resolveImageFromChildren(nodeAny: any, elementId?: string): FxBackgroundSource | null {
  if (!nodeAny || typeof nodeAny.find !== "function") return null;
  try {
    const nodes = nodeAny.find("Image");
    for (const child of nodes ?? []) {
      if (elementId && typeof child?.getAttr === "function") {
        const childElementId = child.getAttr("vbElementId");
        if (childElementId && childElementId !== elementId) continue;
      }
      const image = resolveImageFromNode(child);
      if (image) return image;
    }
  } catch {}
  return null;
}

function resolveCanvasSource(value: unknown): FxBackgroundSource | null {
  if (!value) return null;
  if (value instanceof HTMLVideoElement || value instanceof HTMLImageElement || value instanceof HTMLCanvasElement) {
    return value;
  }
  if (typeof ImageBitmap !== "undefined" && value instanceof ImageBitmap) return value;
  if (typeof OffscreenCanvas !== "undefined" && value instanceof OffscreenCanvas) return value;
  return null;
}

function resolveStageSize(
  node: Konva.Node,
  renderWidth?: number,
  renderHeight?: number
): { width: number; height: number } | null {
  if (
    Number.isFinite(renderWidth) &&
    Number(renderWidth) > 1 &&
    Number.isFinite(renderHeight) &&
    Number(renderHeight) > 1
  ) {
    return { width: Math.round(Number(renderWidth)), height: Math.round(Number(renderHeight)) };
  }
  const stage = node.getStage();
  if (!stage) return null;
  const width = Number(stage.width?.() ?? 0);
  const height = Number(stage.height?.() ?? 0);
  if (!Number.isFinite(width) || width <= 1 || !Number.isFinite(height) || height <= 1) return null;
  return { width: Math.round(width), height: Math.round(height) };
}

function resolveCompositorSize(args: {
  source: FxBackgroundSource;
  node: Konva.Node;
  renderWidth?: number;
  renderHeight?: number;
}): { width: number; height: number } {
  const { source, node, renderWidth, renderHeight } = args;
  if (
    Number.isFinite(renderWidth) &&
    Number(renderWidth) > 1 &&
    Number.isFinite(renderHeight) &&
    Number(renderHeight) > 1
  ) {
    return { width: Math.round(Number(renderWidth)), height: Math.round(Number(renderHeight)) };
  }
  const sourceSize = resolveSourceSize(source);
  if (sourceSize.width > 1 && sourceSize.height > 1) {
    return sourceSize;
  }
  const nodeAny = node as any;
  const width = Number(nodeAny.width?.() ?? 0);
  const height = Number(nodeAny.height?.() ?? 0);
  if (Number.isFinite(width) && width > 1 && Number.isFinite(height) && height > 1) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  return {
    width: 1,
    height: 1,
  };
}

function resolveSourceSize(source: FxBackgroundSource): { width: number; height: number } {
  const width =
    (source as HTMLVideoElement).videoWidth ||
    (source as HTMLImageElement).naturalWidth ||
    source.width ||
    0;
  const height =
    (source as HTMLVideoElement).videoHeight ||
    (source as HTMLImageElement).naturalHeight ||
    source.height ||
    0;
  return {
    width: Math.max(1, Math.round(width || 1)),
    height: Math.max(1, Math.round(height || 1)),
  };
}

function resolveCoverTransform(source: FxBackgroundSource, targetWidth: number, targetHeight: number): FxCoverTransform {
  const src = resolveSourceSize(source);
  const scale = Math.max(targetWidth / Math.max(1, src.width), targetHeight / Math.max(1, src.height));
  const drawWidth = Math.max(1, src.width * scale);
  const drawHeight = Math.max(1, src.height * scale);
  return {
    drawX: (targetWidth - drawWidth) * 0.5,
    drawY: (targetHeight - drawHeight) * 0.5,
    drawWidth,
    drawHeight,
  };
}

function resolveSubjectWindow(args: {
  state: BodyTrackingState | null;
  segmentationState: BodySegmentationState | null;
  packet: FxRenderPacket;
  coverTransform: FxCoverTransform;
  frameWidth: number;
  frameHeight: number;
}): SubjectRect | null {
  const { state, segmentationState, packet, coverTransform, frameWidth, frameHeight } = args;

  const trackedBox = state?.trackedBox ?? null;
  const centerX = clamp01(state?.smoothedTrajectory.x ?? 0.5);
  const centerY = clamp01(state?.smoothedTrajectory.y ?? 0.5);

  const segmentationRect = resolveSegmentationRect(segmentationState, packet);
  const inferred = segmentationRect ?? inferBodyWindowFromFace(trackedBox);
  let normX = inferred?.x ?? centerX - 0.26;
  let normY = inferred?.y ?? centerY - 0.4;
  let normW = inferred?.width ?? 0.52;
  let normH = inferred?.height ?? 0.8;

  const padFactor = clamp(toFinite(packet.params.subjectPadding, 0.18), 0, 0.6);
  normX -= normW * padFactor * 0.5;
  normY -= normH * padFactor * 0.5;
  normW *= 1 + padFactor;
  normH *= 1 + padFactor;

  if (state?.reacquireStatus === "freeze") {
    normW *= 1.08;
    normH *= 1.08;
  } else if (state?.reacquireStatus === "searching") {
    normW *= 1.2;
    normH *= 1.2;
  }

  if (state?.deterministicFallback) {
    normW *= 1.08;
    normH *= 1.08;
  }

  const mappedX = coverTransform.drawX + normX * coverTransform.drawWidth;
  const mappedY = coverTransform.drawY + normY * coverTransform.drawHeight;
  const mappedW = normW * coverTransform.drawWidth;
  const mappedH = normH * coverTransform.drawHeight;
  const x = clamp(mappedX, 0, frameWidth - 1);
  const y = clamp(mappedY, 0, frameHeight - 1);
  const width = clamp(mappedW, 1, frameWidth - x);
  const height = clamp(mappedH, 1, frameHeight - y);

  return { x, y, width, height };
}

function resolveSegmentationRect(state: BodySegmentationState | null, packet: FxRenderPacket): SubjectRect | null {
  if (!state?.personRect) return null;
  if (state.confidence < 0.12) return null;
  if (state.fallback && state.consecutiveMisses > 10) return null;

  const coverage = clamp01(state.mask?.coverage ?? 0);
  const configuredMaxCoverage = clamp(toFinite(packet.params.segmentationMaxCoverage, 0.88), 0.6, 0.995);
  const rectCoverageCap = clamp(
    toFinite(packet.params.segmentationRectMaxCoverage, configuredMaxCoverage - 0.12),
    0.55,
    0.96
  );
  if (coverage > rectCoverageCap) return null;

  const rect = state.personRect;
  const rectArea = clamp01(rect.width) * clamp01(rect.height);
  if (rectArea > 0.9 && coverage > 0.72) return null;
  return {
    x: clamp01(rect.x),
    y: clamp01(rect.y),
    width: clamp(rect.width, 0.12, 1),
    height: clamp(rect.height, 0.18, 1),
  };
}

function resolveSegmentationMaskCandidate(args: {
  state: BodySegmentationState | null;
  packet: FxRenderPacket;
  nowMs: number;
  enabled: boolean;
}): { mask: SegmentationMaskState | null; reason: string } {
  const { state, packet, nowMs, enabled } = args;
  if (!enabled) return { mask: null, reason: "segmentation-disabled" };
  if (!state?.mask) return { mask: null, reason: "segmentation-missing" };
  const minConfidence = clamp(toFinite(packet.params.segmentationMinConfidence, 0.12), 0.05, 0.45);
  if (state.confidence < minConfidence) return { mask: null, reason: "segmentation-low-confidence" };
  const mask = state.mask;
  if (!(mask.width > 0) || !(mask.height > 0)) return { mask: null, reason: "segmentation-size-invalid" };
  if (!mask.alpha || mask.alpha.length < mask.width * mask.height) {
    return { mask: null, reason: "segmentation-alpha-invalid" };
  }
  const minCoverage = clamp(toFinite(packet.params.segmentationMinCoverage, 0.01), 0.001, 0.2);
  if (mask.coverage < minCoverage) return { mask: null, reason: "segmentation-coverage-low" };
  const maxCoverage = clamp(toFinite(packet.params.segmentationMaxCoverage, 0.88), 0.6, 0.995);
  if (mask.coverage > maxCoverage) return { mask: null, reason: "segmentation-coverage-high" };
  const bloom = estimateSegmentationBloom(mask);
  if (bloom.isBlooming) return { mask: null, reason: "segmentation-bloom" };
  const durationDrivenFreshnessMs = Math.max(900, Math.round((packet.durationSec || 0) * 1000));
  const requestedFreshnessMs = toFinite(packet.params.segmentationMaskFreshnessMs, durationDrivenFreshnessMs);
  // Keep blur duration aligned with FX block duration by default.
  // Legacy short freshness values must not prematurely expire long blocks.
  const effectiveFreshnessMs = Math.max(durationDrivenFreshnessMs, requestedFreshnessMs);
  const maxAgeMs = clamp(effectiveFreshnessMs, 120, 240000);
  if (Number.isFinite(mask.generatedAtMs) && nowMs - mask.generatedAtMs > maxAgeMs) {
    return { mask: null, reason: "segmentation-stale" };
  }
  return { mask, reason: "segmentation-ok" };
}

function resolveSegmentationBlendWeight(args: {
  state: BodySegmentationState | null;
  packet: FxRenderPacket;
}): number {
  const { state, packet } = args;
  if (!state?.mask) return 0;
  const threshold = clamp(toFinite(packet.params.segmentationConfidenceThreshold, 0.18), 0.05, 0.6);
  const upper = clamp(threshold + 0.22, threshold + 0.05, 0.95);
  const confidenceWeight = smoothstep(threshold, upper, state.confidence);
  const misses = Math.max(0, state.consecutiveMisses || 0);
  const missWeight = clamp01(1 - Math.max(0, misses - 2) / 14);
  const fallbackWeight = state.fallback ? 0.85 : 1;
  const coverage = clamp01(state.mask.coverage);
  const bloom = estimateSegmentationBloom(state.mask);
  // Segmentation can occasionally bloom toward full-frame and make blur appear "off".
  // Reduce segmentation dominance in that case so fallback window keeps blur visible.
  const coverageWeight = 1 - smoothstep(0.56, 0.84, coverage);
  const bloomWeight = bloom.isBlooming ? 0.12 : 1;
  return clamp01(confidenceWeight * missWeight * fallbackWeight * coverageWeight * bloomWeight);
}

function estimateSegmentationBloom(mask: SegmentationMaskState): {
  isBlooming: boolean;
  edgeRatio: number;
  boundsFill: number;
} {
  const width = Math.max(1, mask.width | 0);
  const height = Math.max(1, mask.height | 0);
  const alpha = mask.alpha;
  if (!alpha || alpha.length < width * height) {
    return { isBlooming: false, edgeRatio: 0, boundsFill: 0 };
  }

  const stride = Math.max(1, Math.round(Math.max(width, height) / 96));
  const activeThreshold = 0.55;

  let edgeSamples = 0;
  let edgeActive = 0;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += stride) {
    for (let x = 0; x < width; x += stride) {
      const idx = y * width + x;
      const v = alpha[idx] ?? 0;
      if (v >= activeThreshold) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
      if (x === 0 || y === 0 || x >= width - stride || y >= height - stride) {
        edgeSamples += 1;
        if (v >= activeThreshold) edgeActive += 1;
      }
    }
  }

  const edgeRatio = edgeSamples > 0 ? edgeActive / edgeSamples : 0;
  const hasBounds = maxX >= minX && maxY >= minY;
  const boundsFill = hasBounds
    ? ((maxX - minX + 1) * (maxY - minY + 1)) / (width * height)
    : 0;
  const isBlooming = edgeRatio > 0.72 && boundsFill > 0.86 && mask.coverage > 0.5;
  return { isBlooming, edgeRatio, boundsFill };
}

function inferBodyWindowFromFace(faceBox: BodyTrackingState["trackedBox"]): SubjectRect | null {
  if (!faceBox) return null;

  const faceW = clamp(faceBox.width, 0.01, 1);
  const faceH = clamp(faceBox.height, 0.01, 1);
  const faceSize = Math.max(faceW, faceH);

  const closeUp = clamp((faceSize - 0.2) / 0.24, 0, 1);
  const widthScale = lerp(2.8, 1.9, closeUp);
  const heightScale = lerp(6.8, 3.8, closeUp);
  const topLift = lerp(0.7, 0.45, closeUp);

  const centerX = faceBox.x + faceW * 0.5;
  const x = centerX - (faceW * widthScale) * 0.5;
  const y = faceBox.y - faceH * topLift;
  const width = faceW * widthScale;
  const height = faceH * heightScale;

  return {
    x: clamp01(x),
    y: clamp01(y),
    width: clamp(width, 0.2, 1),
    height: clamp(height, 0.3, 1),
  };
}

function drawFallbackSubjectMatte(ctx: CanvasRenderingContext2D, subject: SubjectRect): void {
  const cx = subject.x + subject.width * 0.5;
  const cy = subject.y + subject.height * 0.5;
  const headRy = subject.height * 0.22;
  const headRx = subject.width * 0.2;
  const torsoRy = subject.height * 0.36;
  const torsoRx = subject.width * 0.34;
  const shoulderRy = subject.height * 0.2;
  const shoulderRx = subject.width * 0.44;

  ctx.fillStyle = "rgba(255,255,255,1)";
  ctx.beginPath();
  ctx.ellipse(cx, cy - subject.height * 0.23, headRx, headRy, 0, 0, Math.PI * 2);
  ctx.ellipse(cx, cy + subject.height * 0.02, torsoRx, torsoRy, 0, 0, Math.PI * 2);
  ctx.ellipse(cx, cy + subject.height * 0.17, shoulderRx, shoulderRy, 0, 0, Math.PI * 2);
  ctx.fill();
}

function expandSubjectRect(rect: SubjectRect, expandPx: number, nodeW: number, nodeH: number): SubjectRect {
  const px = Math.max(0, expandPx);
  const x = clamp(rect.x - px, 0, nodeW - 1);
  const y = clamp(rect.y - px, 0, nodeH - 1);
  const right = clamp(rect.x + rect.width + px, x + 1, nodeW);
  const bottom = clamp(rect.y + rect.height + px, y + 1, nodeH);
  return {
    x,
    y,
    width: clamp(right - x, 1, nodeW - x),
    height: clamp(bottom - y, 1, nodeH - y),
  };
}

function smoothSubjectWindow(
  prev: SubjectRect | null,
  next: SubjectRect,
  alpha: number,
  displacementClampPx: number
): SubjectRect {
  if (!prev) return next;
  const t = clamp(alpha, 0.08, 1);
  const maxDelta = Math.max(1, displacementClampPx);
  const clampDelta = (from: number, to: number) => clamp(to, from - maxDelta, from + maxDelta);
  return {
    x: clampDelta(prev.x, lerp(prev.x, next.x, t)),
    y: clampDelta(prev.y, lerp(prev.y, next.y, t)),
    width: clampDelta(prev.width, lerp(prev.width, next.width, t)),
    height: clampDelta(prev.height, lerp(prev.height, next.height, t)),
  };
}

function resolveBlurRadius(packet: FxRenderPacket): number {
  if (isFullScreenBlurEnabled(packet)) {
    const amount = clamp(toFinite(packet.params.fullScreenBlurAmount, 52), 0, 100);
    const amountNorm = amount / 100;
    const base = lerp(3, 72, amountNorm);
    const fullScreenIntensity = resolveFullScreenIntensity(packet);
    const fullScreenIntensityScale = lerp(0.48, 1.72, fullScreenIntensity);
    const intensity = clamp(toFinite(packet.params.intensity, 1), 0, 2);
    const opacity = clamp01(packet.opacity);
    const tierScale = packet.qualityTier === "draft" ? 0.9 : packet.qualityTier === "final" ? 1.06 : 1;
    return clamp(base * fullScreenIntensityScale * (0.72 + intensity * 0.38) * (0.72 + opacity * 0.34) * tierScale, 2, 86);
  }
  const base = clamp(toFinite(packet.params.blurRadius, 20), 4, 56);
  const intensity = clamp(toFinite(packet.params.intensity, 1), 0, 2);
  const opacity = clamp01(packet.opacity);
  const tierScale = packet.qualityTier === "draft" ? 0.82 : packet.qualityTier === "final" ? 1.08 : 1;
  return clamp(base * (0.92 + intensity * 0.86) * (0.7 + opacity * 0.38) * tierScale, 4, 72);
}

function shouldUseDraftBlur(packet: FxRenderPacket): boolean {
  if (packet.qualityTier === "draft") return true;
  const tierParam = Number(packet.params.segmentationTier);
  return Number.isFinite(tierParam) && tierParam <= 0.5;
}

function isSegmentationEnabled(packet: FxRenderPacket): boolean {
  if (isFullScreenBlurEnabled(packet)) return false;
  const enabled = Number(packet.params.segmentationEnabled);
  if (!Number.isFinite(enabled)) return true;
  return enabled > 0.5;
}

function isFullScreenBlurEnabled(packet: FxRenderPacket): boolean {
  const enabled = Number(packet.params.fullScreenBlur);
  return Number.isFinite(enabled) && enabled > 0.5;
}

function resolveFullScreenGlow(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.fullScreenGlow, 18), 0, 100) / 100;
}

function resolveFullScreenBloom(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.fullScreenBloom, 14), 0, 100) / 100;
}

function resolveFullScreenVibrance(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.fullScreenVibrance, 9), 0, 100) / 100;
}

function resolveFullScreenIntensity(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.fullScreenIntensity, 58), 0, 100) / 100;
}

function resolveFullScreenContrast(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.fullScreenContrast, 10), 0, 100) / 100;
}

function resolveFullScreenVignette(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.fullScreenVignette, 12), 0, 100) / 100;
}

function resolveMaskDebugMode(packet: FxRenderPacket): MaskDebugMode {
  const raw = Number(packet.params.debugMaskOverlay);
  if (!Number.isFinite(raw) || raw <= 0.5) return "off";
  if (raw >= 2.5) return "post";
  if (raw >= 1.5) return "raw";
  return "final";
}

function drawMaskDebugOverlay(args: {
  compositeBuffer: CanvasBundle;
  maskSource: CanvasImageSource;
  width: number;
  height: number;
}): void {
  const { compositeBuffer, maskSource, width, height } = args;
  const ctx = compositeBuffer.ctx;
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 0.38;
  ctx.fillStyle = "rgba(0,0,0,0.4)";
  ctx.fillRect(0, 0, width, height);
  ctx.globalAlpha = 0.78;
  ctx.drawImage(maskSource, 0, 0, width, height);
  ctx.globalCompositeOperation = "source-atop";
  ctx.fillStyle = "rgba(52,211,153,0.78)";
  ctx.fillRect(0, 0, width, height);
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 0.24;
  ctx.strokeStyle = "rgba(52,211,153,0.95)";
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, Math.max(0, width - 1), Math.max(0, height - 1));
  ctx.restore();
}

function resolveSubjectExpandPx(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.subjectExpandPx, 4), 2, 6);
}

function resolveSubjectFeatherPx(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.subjectFeatherPx, 4), 0, 24);
}

function resolveTemporalSmoothingAlpha(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.subjectTemporalSmoothing, 0.28), 0.08, 0.7);
}

function resolveSubjectDisplacementClampPx(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.subjectDisplacementClampPx, 18), 4, 64);
}

function resolveMaskTemporalBlend(packet: FxRenderPacket): number {
  return clamp(toFinite(packet.params.segmentationTemporalBlend, 0.28), 0.08, 0.65);
}

function toFinite(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge1 <= edge0) return x >= edge0 ? 1 : 0;
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function lerp(from: number, to: number, alpha: number): number {
  return from + (to - from) * clamp01(alpha);
}
